import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";

export const baseSf = (sf) => { const m = (sf || "").toUpperCase().match(/SF[\s\-/_.]*(\d{3,6})/); return m ? `SF-${m[1]}` : sf; };
const iteration = (sf) => { const m = /\/(\d+)\s*$/.exec(sf || ""); return m ? Number(m[1]) : 1; };

export const FIELDS = {
  due_amount: { label: "Amount due", type: "number", get: (l) => l.due_amount },
  balance: { label: "Loan balance", type: "number", get: (l) => l.principal_balance },
  in_arrears: { label: "In arrears", type: "bool", get: (l, f) => f?.in_arrears ?? (l.past_due > 0) },
  arrears_amount: { label: "Arrears amount", type: "number", get: (l, f) => f?.arrears_amount ?? l.past_due },
  days_past_due: { label: "Days past due", type: "number", get: (l, f) => f?.days_past_due ?? l.days_past_due },
  restructured: { label: "Renewed / restructured", type: "bool", get: (l, f) => f?.restructured ?? iteration(l.sf_number) > 1 },
  iteration: { label: "SF iteration (/N)", type: "number", get: (l, f) => f?.iteration ?? iteration(l.sf_number) },
  released_date: { label: "Disbursed date", type: "date", get: (l, f) => f?.released_date },
  days_since_disbursed: { label: "Days since disbursement", type: "number", get: (l, f) => f?.days_since_disbursed },
  loan_product: { label: "Loan product", type: "text", get: (l, f) => l.loan_product || f?.loan_product },
  has_mobile: { label: "Has mobile", type: "bool", get: (l) => !!l.mobile },
  has_email: { label: "Has email", type: "bool", get: (l) => !!l.email },
  chased_today: { label: "Chased today", type: "bool", get: (l, f, chased) => !!chased?.[baseSf(l.sf_number)] },
  has_file: { label: "Has portal file", type: "bool", get: (l, f) => !!f?.has_file },
  legal: { label: "With Legal", type: "bool", get: (l, f) => !!f?.legal },
};

export const OPERATORS = {
  number: [["gt", "more than"], ["gte", "at least"], ["lt", "less than"], ["lte", "at most"], ["eq", "equals"], ["between", "between"]],
  bool: [["is_true", "is yes"], ["is_false", "is no"]],
  date: [["before", "before"], ["after", "after"], ["between", "between"]],
  text: [["contains", "contains"], ["not_contains", "doesn't contain"], ["eq", "is"]],
};

export const newRule = (field = "due_amount") => ({ id: Math.random().toString(36).slice(2, 8), field, op: OPERATORS[FIELDS[field].type][0][0], value: "", value2: "" });

export function matchesRule(rule, loan, facts, chased) {
  const def = FIELDS[rule.field];
  if (!def) return true;
  const v = def.get(loan, facts?.[loan.sf_number], chased);
  if (def.type === "bool") return rule.op === "is_true" ? !!v : !v;
  if (rule.value === "" || rule.value == null) return true;
  if (v == null || v === "") return false;
  if (def.type === "text") {
    const a = String(v).toLowerCase(), b = String(rule.value).toLowerCase();
    return rule.op === "contains" ? a.includes(b) : rule.op === "not_contains" ? !a.includes(b) : a === b;
  }
  if (def.type === "date") {
    const a = String(v).slice(0, 10), b = String(rule.value).slice(0, 10), c = String(rule.value2 || "").slice(0, 10);
    return rule.op === "before" ? a < b : rule.op === "after" ? a > b : (a >= b && (!c || a <= c));
  }
  const n = Number(v), x = Number(rule.value), y = Number(rule.value2);
  if (Number.isNaN(n) || Number.isNaN(x)) return false;
  switch (rule.op) {
    case "gt": return n > x;
    case "gte": return n >= x;
    case "lt": return n < x;
    case "lte": return n <= x;
    case "eq": return Math.abs(n - x) < 0.005;
    case "between": return n >= x && (Number.isNaN(y) || rule.value2 === "" || n <= y);
    default: return true;
  }
}

export const applyRules = (rules, loans, facts, chased) => loans.filter((l) => rules.every((r) => matchesRule(r, l, facts, chased)));

export const ruleText = (r) => {
  const def = FIELDS[r.field];
  const op = OPERATORS[def.type].find(([k]) => k === r.op)?.[1] || r.op;
  if (def.type === "bool") return `${def.label} ${op}`;
  return `${def.label} ${op} ${r.value}${r.op === "between" && r.value2 ? ` – ${r.value2}` : ""}`;
};

function ValueInput({ rule, def, onChange, idx }) {
  if (def.type === "bool") return null;
  const type = def.type === "date" ? "date" : def.type === "number" ? "number" : "text";
  return (
    <>
      <Input type={type} value={rule.value} onChange={(e) => onChange({ value: e.target.value })} className="h-8 w-36 text-xs" placeholder={def.type === "text" ? "e.g. FTLA" : ""} data-testid={`prechase-filter-value-${idx}`} />
      {rule.op === "between" && (
        <>
          <span className="text-xs text-slate-400">and</span>
          <Input type={type} value={rule.value2} onChange={(e) => onChange({ value2: e.target.value })} className="h-8 w-36 text-xs" data-testid={`prechase-filter-value2-${idx}`} />
        </>
      )}
    </>
  );
}

export function FilterBuilder({ rules, setRules, matchCount, totalCount, factsLoading }) {
  const update = (id, patch) => setRules((rs) => rs.map((r) => {
    if (r.id !== id) return r;
    const next = { ...r, ...patch };
    if (patch.field && patch.field !== r.field) { next.op = OPERATORS[FIELDS[patch.field].type][0][0]; next.value = ""; next.value2 = ""; }
    return next;
  }));
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2.5" data-testid="prechase-filter-builder">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span className="font-medium text-slate-700">Filter</span>
        <span data-testid="prechase-filter-count">{rules.length ? `${matchCount} of ${totalCount} loans match` : `all ${totalCount} loans`}</span>
        {factsLoading && <span className="text-slate-400">· loading arrears / disbursement facts…</span>}
        <div className="ml-auto flex gap-1">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setRules((rs) => [...rs, newRule()])} data-testid="prechase-add-filter-rule-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add filter</Button>
          {rules.length > 0 && <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-500" onClick={() => setRules([])} data-testid="prechase-clear-filters-btn">Clear</Button>}
        </div>
      </div>
      {rules.map((r, idx) => {
        const def = FIELDS[r.field];
        return (
          <div key={r.id} className="mt-2 flex flex-wrap items-center gap-2" data-testid={`prechase-filter-row-${idx}`}>
            <span className="w-9 text-[11px] uppercase tracking-wide text-slate-400">{idx === 0 ? "where" : "and"}</span>
            <Select value={r.field} onValueChange={(v) => update(r.id, { field: v })}>
              <SelectTrigger className="h-8 w-48 text-xs" data-testid={`prechase-filter-field-${idx}`}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(FIELDS).map(([k, d]) => <SelectItem key={k} value={k} className="text-xs">{d.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={r.op} onValueChange={(v) => update(r.id, { op: v })}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid={`prechase-filter-operator-${idx}`}><SelectValue /></SelectTrigger>
              <SelectContent>{OPERATORS[def.type].map(([k, label]) => <SelectItem key={k} value={k} className="text-xs">{label}</SelectItem>)}</SelectContent>
            </Select>
            <ValueInput rule={r} def={def} idx={idx} onChange={(patch) => update(r.id, patch)} />
            <button type="button" onClick={() => setRules((rs) => rs.filter((x) => x.id !== r.id))} className="ml-auto text-slate-400 hover:text-slate-700" aria-label="Remove filter" data-testid={`prechase-filter-remove-btn-${idx}`}><X className="w-3.5 h-3.5" /></button>
          </div>
        );
      })}
    </div>
  );
}
